<!-- DayMenuView.vue -->
<script setup lang="ts">
import { usePermissions } from '@/composables/usePermissions';
import type { MessageSchema } from '@/i18n';
import { usePlanningStore } from '@/stores/planningStore';
import { useUserStore } from '@/stores/useUserStore';
import type { PlannedMenu } from '@/types/planning';
import { onMounted, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import MenuView from './MenuView.vue';
const user = ref(useUserStore().user);
const restaurantId = user.value?.restaurants[0]?.id || null;

const { can } = usePermissions();

// Define props for the component
const props = defineProps<{
  date: Date;
}>();
const planningStore = ref(usePlanningStore());
const plannedMenusForDay: Ref<PlannedMenu[]> = ref([]);
const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const router = useRouter();

async function fetchMenuForDay() {
  const formattedDate = props.date.toISOString().split('T')[0];
  plannedMenusForDay.value = planningStore.value.getMenusByDate(formattedDate);
}
// Define a list of dishes for the current day

const containerRef = ref<HTMLDivElement | null>(null);
const showLeftShadow = ref(false);
const showRightShadow = ref(false);

function handleScroll() {
  if (!containerRef.value) return;
  const scrollLeft = containerRef.value.scrollLeft;
  const maxScrollLeft = containerRef.value.scrollWidth - containerRef.value.clientWidth;

  showLeftShadow.value = scrollLeft > 0;
  showRightShadow.value = scrollLeft < maxScrollLeft;
}

onMounted(() => {
  fetchMenuForDay();
  if (containerRef.value) {
    containerRef.value.addEventListener('scroll', handleScroll);
  }
});

// Add a new dish to the list
function addPlannedMenu() {
  const newMenu: PlannedMenu = {
    id: -1, // Placeholder ID, will be replaced by actual ID on server response
    tempId: Date.now(), // Temporary ID for local use
    name: t('planning.menuOfTheDay'),
    date: props.date.toISOString().split('T')[0],
    price: 0,
    spec: '',
    restaurant_id: 'default_restaurant_id',
    menu_courses: [],
  };

  planningStore.value.addPlannedMenu(newMenu, restaurantId, router);
}

function isToday(date: Date): boolean {
  const today = new Date();
  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
}

watch(
  () => planningStore.value.plannedMenus,
  () => {
    fetchMenuForDay();
  },
  { deep: true },
);

watch(
  () => props.date,
  () => {
    fetchMenuForDay();
  },
);
</script>

<template>
  <!-- <h3 class="font-semibold">{{ date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) }}</h3> -->
  <div class="my-4 flex w-full flex-row items-center">
    <!-- Left side for the day and number -->
    <div
      :class="[
        'text-center',
        'p-2',
        'rounded-md',
        'flex',
        'flex-col',
        'justify-center',
        'w-1/12',
        'min-w-12',
        'mx-4',
        isToday(props.date) ? 'bg-accent text-white' : 'text-primary',
      ]"
    >
      <span class="text-sm">{{ t(`common.dayShort.${props.date.toLocaleDateString('en-US', { weekday: 'short' })}`) }}</span>
      <span class="font-bold text-lg">{{ props.date.getDate() }}</span>
    </div>

    <!-- Outer container for scrollable content with shadows -->
    <div class="scroll-container relative w-9/12 flex-grow">
      <div ref="containerRef" class="hide-scrollbar p flex items-center overflow-x-auto" @scroll="handleScroll">
        <div class="flex flex-row gap-4">
          <div v-for="menu in plannedMenusForDay" :key="menu.id">
            <MenuView :date="props.date" :planned-menu="menu"></MenuView>
            <!-- other menu details -->
          </div>
          <!-- <MenuView :date="props.date"></MenuView>
          <MenuView :date="props.date"></MenuView>
          <MenuView :date="props.date"></MenuView> -->
          <!-- Add more MenuView components here -->
          <div v-if="can('planning', 'create')" class="relative cursor-pointer" @click="addPlannedMenu">
            <div class="absolute inset-0 z-10 flex items-center justify-center">
              <button class="flex aspect-square w-10 items-center justify-center rounded-full bg-accent p-2 text-white">
                <!-- Icon plus -->
                <i class="pi pi-plus" style="font-size: 1rem"></i>
              </button>
            </div>
            <div class="relative mx-2 my-4 flex h-56 w-48 flex-grow items-start rounded-xl bg-primary-200 px-4 py-8 opacity-25 shadow-lg">
              <div
                class="absolute -top-4 left-1/2 max-w-36 -translate-x-1/2 overflow-hidden truncate text-ellipsis whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-center font-bold text-xs text-black"
              >
                <p class="h-4 w-24"></p>
              </div>
              <div class="flex flex-col justify-around gap-3 text-sm"></div>
            </div>
          </div>
        </div>

        <!-- "Plus" button to add a new dish -->
      </div>

      <!-- Shadows for overflow on the left and right -->
      <div v-if="showLeftShadow" class="left-shadow"></div>
      <div v-if="showRightShadow" class="right-shadow"></div>
    </div>
  </div>
</template>

<style scoped>
/* Hide the scroll bar but still allow horizontal scrolling */
.hide-scrollbar {
  overflow-x: auto;
  scrollbar-width: none;
  /* Firefox */
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, Opera */
}

/* Outer container to hold the scrollable area and the shadows */
/* .scroll-container {
  position: relative;
  max-width: 100%;
  width: 100%;
} */

/* Inner shadow for overflow */
.left-shadow,
.right-shadow {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 6px;
  /*10*/
  pointer-events: none;
  /* z-index: 0; */
}

.left-shadow {
  left: 0;
  /* background: linear-gradient(to right, rgba(255, 255, 255, 1), transparent); */
  background: linear-gradient(to right, rgb(193 214 224), rgba(193, 214, 224, 0));
}

.right-shadow {
  right: 0;
  /* background: linear-gradient(to left, rgba(255, 255, 255, 1), transparent); */
  background: linear-gradient(to left, rgb(193 214 224), rgba(193, 214, 224, 0));
}
</style>
